import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
  type TableColumnsType,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import { adminLinks } from '../../lib/adminLinks';
import { downloadText } from '../../lib/csv';
import { QueryError } from '../../lib/QueryState';
import { dayParam, intParam, pageSizeParam, useUrlState } from '../../lib/urlState';
import { LogDetailDrawer } from './LogDetailDrawer';
import {
  CATEGORY_LABEL,
  FILTER_KEYS,
  SEVERITY_COLOR,
  SOURCE_LABEL,
  STATUS_COLOR,
  cleanFilters,
  eventLabel,
  formatLogTime,
  refName,
  useLogsMeta,
  useLogsPage,
  useLogsSummary,
  type LogRefs,
  type LogRow,
  type LogsFilters,
} from './logsApi';

const PAGE_SIZES = [50, 25, 100] as const;
const SEVERITY_FILTERS = [
  { value: 'INFO', label: 'Info' },
  { value: 'NOTICE', label: 'Notice' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Error (incl. critical)' },
  { value: 'CRITICAL', label: 'Critical' },
];

function actorCell(r: LogRow, refs?: LogRefs) {
  if (r.actorType === 'system') return <Typography.Text type="secondary">System</Typography.Text>;
  const kind = r.actorType === 'admin' ? 'admin' : 'user';
  const name = refName(refs, kind, r.actorId);
  const label = r.actorType === 'admin' ? 'Admin' : r.actorType === 'listener' ? 'Listener' : 'User';
  const to = r.actorId && r.actorType !== 'admin' ? adminLinks.user(r.actorId) : null;
  const text = name ?? (r.actorId ? r.actorId.slice(0, 8) : '');
  return (
    <Space size={4} direction="vertical" style={{ lineHeight: 1.2 }}>
      <Tag color={r.actorType === 'admin' ? 'magenta' : r.actorType === 'listener' ? 'purple' : 'blue'}>{label}</Tag>
      {text && (to ? <Link to={to} onClick={(e) => e.stopPropagation()}>{text}</Link> : <span style={{ fontSize: 12 }}>{text}</span>)}
    </Space>
  );
}

function targetCell(r: LogRow, refs?: LogRefs) {
  if (r.callId) {
    return (
      <Link to={adminLinks.call(r.callId)} onClick={(e) => e.stopPropagation()}>
        Call {r.callId.slice(0, 8)}
      </Link>
    );
  }
  if (r.paymentId && r.targetType === 'PaymentOrder') {
    return (
      <Link to={adminLinks.recharge(r.paymentId)} onClick={(e) => e.stopPropagation()}>
        Recharge {r.paymentId.slice(0, 8)}
      </Link>
    );
  }
  if (r.listenerId) {
    return (
      <Link to={adminLinks.listener(r.listenerId)} onClick={(e) => e.stopPropagation()}>
        Listener {refName(refs, 'listener', r.listenerId) ?? r.listenerId.slice(0, 8)}
      </Link>
    );
  }
  if (r.userId) {
    return (
      <Link to={adminLinks.user(r.userId)} onClick={(e) => e.stopPropagation()}>
        User {refName(refs, 'user', r.userId) ?? r.userId.slice(0, 8)}
      </Link>
    );
  }
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {r.targetType}
      {r.targetId ? ` ${r.targetId.slice(0, 8)}` : ''}
    </Typography.Text>
  );
}

/**
 * Logs / Activity — every recorded business and (with logs.view_sensitive)
 * technical event, filtered, searched and paged ON THE SERVER. Applied filters,
 * page, page size and the open row live in the URL (lib/urlState), so Back,
 * refresh and a pasted link all show the same view. Filter edits are drafted
 * locally and committed with Apply.
 */
export function LogsPage() {
  const { get, set, params } = useUrlState();
  const { can } = usePermissions();
  const meta = useLogsMeta();
  const canSensitive = meta.data?.canViewSensitive ?? false;

  const applied: LogsFilters = useMemo(() => {
    const f: LogsFilters = {};
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) f[k] = k === 'from' || k === 'to' ? dayParam(v) || undefined : v;
    }
    return cleanFilters(f);
  }, [params]);
  const page = intParam(get('page'), 1);
  const pageSize = pageSizeParam(get('size'), PAGE_SIZES);
  const openId = get('log');

  // Draft filters, committed on Apply. Back / forward resets the draft.
  const [draft, setDraft] = useState<LogsFilters>(applied);
  useEffect(() => setDraft(applied), [applied]);
  const patchDraft = (p: Partial<LogsFilters>) => setDraft((d) => ({ ...d, ...p }));
  const dirty = JSON.stringify(cleanFilters(draft)) !== JSON.stringify(applied);

  const apply = (f: LogsFilters = draft) => {
    const clean = cleanFilters(f);
    set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, clean[k] ?? null])), page: null });
  };
  const reset = () => {
    setDraft({});
    set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, null])), page: null });
  };

  const list = useLogsPage(applied, page, pageSize);
  const summary = useLogsSummary(applied);
  const rows = list.data?.data ?? [];
  const refs = list.data?.refs;

  // The open row: from the page when it is on it, else fetched by id (a pasted link).
  const onPage = rows.find((r) => r.id === openId) ?? null;
  const single = useQuery({
    queryKey: ['admin', 'logs', 'detail', openId],
    queryFn: async () => (await api.get<{ data: LogRow; refs: LogRefs }>(`/admin/logs/${openId}`)).data,
    enabled: !!openId && !onPage && !list.isLoading,
  });
  const openRow = onPage ?? single.data?.data ?? null;

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get<string>('/admin/logs/export', { params: applied, responseType: 'text' });
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-');
      downloadText(`activity-logs-${stamp}.csv`, res.data);
      const truncated = String(res.headers['x-export-truncated'] ?? '0') === '1';
      if (truncated) message.warning(`Export capped at ${meta.data?.limits.exportMax ?? 10000} rows — narrow the filters for the rest.`);
      else message.success(`Exported ${res.headers['x-export-rows'] ?? ''} rows.`);
    } catch {
      message.error('Export failed. Try narrower filters.');
    } finally {
      setExporting(false);
    }
  };

  const eventOptions = useMemo(() => {
    const events = meta.data?.events ?? [];
    const byCat = new Map<string, Array<{ value: string; label: string }>>();
    for (const e of events) {
      if (draft.event_category && e.category !== draft.event_category) continue;
      byCat.set(e.category, [...(byCat.get(e.category) ?? []), { value: e.type, label: `${e.label} (${e.type})` }]);
    }
    const groups = [...byCat.entries()].map(([cat, options]) => ({ label: CATEGORY_LABEL[cat] ?? cat, options }));
    if (!draft.event_category || draft.event_category === 'ADMIN') {
      groups.push({
        label: 'Admin (grouped)',
        options: (meta.data?.adminAliases ?? []).map((a) => ({ value: a, label: `${eventLabel({ eventType: a, eventLabel: null })} (${a})` })),
      });
    }
    return groups;
  }, [meta.data, draft.event_category]);

  const columns: TableColumnsType<LogRow> = [
    { title: 'Time', dataIndex: 'createdAt', width: 170, render: (s: string) => formatLogTime(s) },
    {
      title: 'Event',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 500 }}>{eventLabel(r)}</span>
          {r.message && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: r.message }}>
              {r.message}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'eventCategory',
      width: 120,
      responsive: ['md'],
      render: (c: string, r) => (
        <Space size={4} wrap>
          <Tag>{CATEGORY_LABEL[c] ?? c}</Tag>
          {r.severity !== 'INFO' && <Tag color={SEVERITY_COLOR[r.severity]}>{r.severity}</Tag>}
        </Space>
      ),
    },
    { title: 'Actor', width: 140, render: (_: unknown, r) => actorCell(r, refs) },
    { title: 'Target', width: 170, responsive: ['lg'], render: (_: unknown, r) => targetCell(r, refs) },
    { title: 'Status', dataIndex: 'status', width: 100, render: (s: LogRow['status']) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
    { title: 'Source', dataIndex: 'source', width: 120, responsive: ['xl'], render: (s: string) => SOURCE_LABEL[s] ?? s },
    {
      title: 'Request ID',
      dataIndex: 'requestId',
      width: 110,
      responsive: ['xl'],
      render: (id: string | null) =>
        id ? (
          <Typography.Text copyable={{ text: id }} style={{ fontSize: 11 }} title={id}>
            {id.slice(0, 8)}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      width: 70,
      render: (_: unknown, r) => (
        <a
          onClick={(e) => {
            e.stopPropagation();
            set({ log: r.id }, { push: true });
          }}
        >
          Details
        </a>
      ),
    },
  ];

  const s = summary.data;
  const isFiltered = Object.keys(applied).length > 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
        <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
          Activity logs
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void Promise.all([list.refetch(), summary.refetch()])}>
            Refresh
          </Button>
          {can('logs.export') && (
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={() => void exportCsv()}>
              Export CSV
            </Button>
          )}
        </Space>
      </Space>

      {/* Summary for the applied filters (date range included). */}
      <Row gutter={[12, 12]}>
        {[
          { label: 'Total events', value: s?.total, color: undefined },
          { label: 'Errors', value: s?.errors, color: s?.errors ? '#ff4d4f' : undefined },
          { label: 'Warnings', value: s?.warnings, color: s?.warnings ? '#fa8c16' : undefined },
          { label: 'Successful', value: s?.successful, color: undefined },
          { label: 'Failed', value: s?.failed, color: s?.failed ? '#ff4d4f' : undefined },
        ].map((c) => (
          <Col key={c.label} xs={12} sm={8} md={4}>
            <Card size="small" loading={summary.isLoading}>
              <Statistic title={c.label} value={c.value ?? 0} valueStyle={c.color ? { color: c.color } : undefined} />
            </Card>
          </Col>
        ))}
      </Row>
      {s && (
        <Space wrap size={[6, 6]}>
          {Object.entries(s.byCategory)
            .filter(([, n]) => n > 0)
            .map(([cat, n]) => (
              <Tag.CheckableTag
                key={cat}
                checked={applied.event_category === cat}
                onChange={(on) => apply({ ...applied, event_category: on ? cat : undefined, event_type: undefined })}
              >
                {CATEGORY_LABEL[cat] ?? cat} · {n}
              </Tag.CheckableTag>
            ))}
        </Space>
      )}

      <Card>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Input.Search
            aria-label="Search logs"
            placeholder="Search logs — user / listener / admin / call / payment / transaction / request / correlation id, event type or message"
            value={draft.search ?? ''}
            onChange={(e) => patchDraft({ search: e.target.value })}
            onSearch={(v) => apply({ ...draft, search: v })}
            allowClear
            enterButton
          />
          <Space wrap>
            {/* Uncontrolled (the panel has no direct dayjs dependency); remounted
                when the applied range changes so Back / Reset clear it. */}
            <DatePicker.RangePicker
              key={`${applied.from ?? ''}|${applied.to ?? ''}`}
              aria-label="Date range"
              onChange={(_d, [f, t]) => patchDraft({ from: f || undefined, to: t || undefined })}
              allowEmpty={[true, true]}
            />
            {(applied.from || applied.to) && (
              <Tag closable color="blue" onClose={() => apply({ ...applied, from: undefined, to: undefined })}>
                {applied.from === applied.to ? applied.from : `${applied.from ?? '…'} → ${applied.to ?? '…'}`}
              </Tag>
            )}
            <Select
              allowClear
              showSearch
              aria-label="Event type"
              placeholder="Event type"
              style={{ minWidth: 230 }}
              value={draft.event_type}
              onChange={(v) => patchDraft({ event_type: v })}
              options={eventOptions}
              optionFilterProp="label"
            />
            <Select
              allowClear
              aria-label="Category"
              placeholder="Category"
              style={{ minWidth: 150 }}
              value={draft.event_category}
              onChange={(v) => patchDraft({ event_category: v, event_type: undefined })}
              options={(meta.data?.categories ?? []).map((c) => ({ value: c, label: CATEGORY_LABEL[c] ?? c }))}
            />
            <Select
              allowClear
              aria-label="Severity"
              placeholder="Severity: all"
              style={{ minWidth: 150 }}
              value={draft.severity}
              onChange={(v) => patchDraft({ severity: v })}
              options={SEVERITY_FILTERS}
            />
            <Select
              allowClear
              aria-label="Status"
              placeholder="Status"
              style={{ minWidth: 120 }}
              value={draft.status}
              onChange={(v) => patchDraft({ status: v })}
              options={(meta.data?.statuses ?? []).map((v) => ({ value: v, label: v.charAt(0) + v.slice(1).toLowerCase() }))}
            />
            <Select
              allowClear
              aria-label="Source"
              placeholder="Source"
              style={{ minWidth: 150 }}
              value={draft.source}
              onChange={(v) => patchDraft({ source: v })}
              options={(meta.data?.sources ?? []).map((v) => ({ value: v, label: SOURCE_LABEL[v] ?? v }))}
            />
            {canSensitive && (
              <Select
                allowClear
                aria-label="Log kind"
                placeholder="Business + technical"
                style={{ minWidth: 170 }}
                value={draft.kind}
                onChange={(v) => patchDraft({ kind: v })}
                options={[
                  { value: 'audit', label: 'Business only' },
                  { value: 'system', label: 'Technical only' },
                ]}
              />
            )}
          </Space>
          <Space wrap>
            <Input
              aria-label="User id"
              placeholder="User id"
              value={draft.user_id ?? ''}
              onChange={(e) => patchDraft({ user_id: e.target.value })}
              style={{ width: 300 }}
              allowClear
            />
            <Input
              aria-label="Listener id"
              placeholder="Listener id"
              value={draft.listener_id ?? ''}
              onChange={(e) => patchDraft({ listener_id: e.target.value })}
              style={{ width: 300 }}
              allowClear
            />
            <Input
              aria-label="Admin id"
              placeholder="Admin id"
              value={draft.admin_id ?? ''}
              onChange={(e) => patchDraft({ admin_id: e.target.value })}
              style={{ width: 300 }}
              allowClear
            />
          </Space>
          <Space wrap>
            <Button type="primary" onClick={() => apply()} disabled={!dirty}>
              Apply filters
            </Button>
            <Button onClick={reset} disabled={!isFiltered && !dirty}>
              Reset
            </Button>
            {(['call_id', 'payment_id', 'transaction_id', 'request_id', 'correlation_id'] as const)
              .filter((k) => applied[k])
              .map((k) => (
                <Tag key={k} closable color="blue" onClose={() => apply({ ...applied, [k]: undefined })}>
                  {k.replace('_id', '')}: {applied[k]!.slice(0, 12)}
                </Tag>
              ))}
          </Space>
        </Space>
      </Card>

      <Card>
        {list.isError ? (
          <QueryError error={list.error} what="the activity logs" onRetry={() => void list.refetch()} />
        ) : (
          <>
            {!canSensitive && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="Showing business activity. Technical / error logs need the “View technical logs” permission."
              />
            )}
            <Table
              rowKey="id"
              size="middle"
              loading={list.isFetching}
              dataSource={rows}
              columns={columns}
              scroll={{ x: 'max-content' }}
              onRow={(r) => ({ onClick: () => set({ log: r.id }, { push: true }), style: { cursor: 'pointer' } })}
              locale={{ emptyText: isFiltered ? 'No events match these filters.' : 'No events recorded yet.' }}
              pagination={{
                current: page,
                pageSize,
                total: list.data?.pagination.total ?? 0,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100],
                showTotal: (total) => `${total.toLocaleString()} events`,
                onChange: (p, size) => set({ page: size !== pageSize ? null : p > 1 ? p : null, size: size === 50 ? null : size }),
              }}
            />
          </>
        )}
      </Card>

      <LogDetailDrawer
        row={openRow}
        refs={onPage ? refs : single.data?.refs}
        open={!!openId && !!openRow}
        onClose={() => set({ log: null })}
        onFilter={(p) => {
          setDraft({ ...p });
          set({ ...Object.fromEntries(FILTER_KEYS.map((k) => [k, null])), ...p, page: null, log: null });
        }}
      />
    </Space>
  );
}
